export default `# Create React App

Once you open the *Sweet Pumpkins* in your editor, you can inspect the project's file structure.

Let me give you a quick run through of all the elements.

The **node_modules** directory, present in all React applications, stores packages required to run the application.
If you open it, you will see a lot of directories. Each
directory includes one package. And this is probably the first and the last time when you have to look inside of this directory.

We will manage our packages using either \`yarn\` or \`npm\` and they will be automatically added to or removed from the *node_modules* directory.

In the **public** directory, we will keep all the files that should be easily accessible to the browser.
You should see here:
- *favicon.ico* - this is a small image that your browser displays in a tab next to the title of the website
- *index.html* - the same, although a more complete *index.html* file that we were using in the previous chapters. When you inspect it, you will find a familiar \`div\` element with an id \`root\`. Inside of this \`div\` you will render the React application.
- *manifest.json* file is used for [Progressive Web Apps](https://developers.google.com/web/progressive-web-apps/) (PWAs). We will not use this functionality in this course but you may need to use it in the future.

The **src** (source code) directory is where you will keep all your React components. When you inspect the *index.js* file you can see the familiar
structure from the previous chapter. The only difference is the \`registerServiceWorker()\` line. Service workers are used
for PWAs and should work properly everywhere except of iOS devices. To avoid problems after deploying our app, comment
out both lines.

The easiest way to comment some code in Atom is to place your cursor in the line that you want to comment out
and press \`⌘ + /\` (Mac) or \`Ctrl + /\` (Windows). If you want to comment more than one line, select
 the range and press \`⌘ + /\` (Mac) or \`Ctrl + /\` (Windows).
\`\`\`plainJavascript
// import registerServiceWorker from './registerServiceWorker';
...

// registerServiceWorker();
\`\`\`

The *index.js* file also imports a main stylesheet *index.css*. Open the \`index.css\` and replace its content with:

\`\`\`plainCss
* {
  margin: 0;
  padding: 0;
  font-family: sans-serif;
  box-sizing: border-box;
}
\`\`\`

It will reset margins, paddings, and the box-sizing model and changes the default \`font-family\` to *sans-serif*.

The *index.js* imports and renders the \`<App />\` component. Let's check the \`App\` component next.
It looks like a regular React class component that displays the \`header\` and a paragraph of text.

It also imports CSS styles from *App.css* and attaches appropriate classes to the code.

It the root directory of the project, you can find the *package.json* file. It is used by package managers \`npm\` and \`yarn\`.
Here is the place to store important metadata about the project. This is also where to list
all dependencies. At the moment, we only have two packages installed:

\`\`\`plainJavascript
"dependencies": {
  "react": "^16.1.1",
  "react-dom": "^16.1.1"
}
\`\`\`

When you add, update or remove a package using either \`npm\` or \`yarn\`, the package manager will update the *package.json* file for you automatically.

The *yarn.lock* file is used exclusively by the \`yarn\` to keep track of the exact versions of packages that the project relies on. It's managed by the \`yarn\` and
you shouldn't touch it.

The project is all set up and we can start the server:

\`\`\`plainJavascript
yarn start
\`\`\`

After a while, the browser window will open automatically and you will see the application rendered successfully:

![Create React App main screen](https://sweetpumpkins.codecamps.com/CRA-main-app-min.jpg)

If that doesn't happen, open the browser and in the address bar write \`localhost:3000\` and press enter.

You've set up a boilerplate and checked that it works. Let's find out what we can build on top of it.

`;



// WEBPACK FOOTER //
// ./src/book/chapters/create-react-app.js